import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, FlatList, Text, ListRenderItem, TouchableOpacity, Platform } from 'react-native';
import { showConfirm } from '@/utils/common/alert';
import { useNavigation } from '@react-navigation/native';
import { DesktopAdminLayout } from '@/components/common/admin/desktop/DesktopAdminLayout';
import { DesktopPageHeader } from '@/components/common/admin/desktop/DesktopPageHeader';
import { InlineEmptyMessage } from '@/components';
import { ConversationListSkeleton } from '@/components/admin/messages/ConversationListSkeleton';
import { EMPTY_MESSAGES } from '@/constants/admin';
import { getFilterEmptyState, getNoResultsState } from '@/utils/admin/messages/emptyState';
import { useConversationUtils } from '@/hooks/admin/useMessages';
import { ChatWindow } from '@/components/admin/messages/ChatWindow';
import { DesktopSidePanel } from '@/components/common/admin/desktop/panel/DesktopSidePanel';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { DesktopChatOptions } from '@/components/admin/messages/desktop/DesktopChatOptions';
import { ConversationDetailsPanel } from '@/components/admin/messages/desktop/ConversationDetailsPanel';
import { useConversations, useMessageFilters } from '@/hooks/admin/useMessages';
import { useGetConversationQuery } from '@/store/api/admin/messagesApi';
import { IConversation, IConversationAccount, FilterType, FILTER_VALUES } from '@/types/admin/messages';
import { useDebouncedValue } from '@/hooks/common/useDebouncedValue';
import { AnchorRect } from '@/hooks/common/useAnchoredPosition';
import { SVG_COMPONENTS } from '@/assets/admin';
import { ThreadOwnerModal } from '@/components/admin/messages/ThreadOwnerModal';
import { useArchiveConversationMutation } from '@/store/api/admin/messagesApi';
import { useToast, useErrorToast } from '@/contexts/admin/ToastContext';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import {
  GlassCard,
  ChatGlassConversationCard,
  ChatGlassListHeader,
  TabSegment,
  TabSegmentItem,
} from '@/components/shared/redesign';

export const DesktopMessagesView: React.FC = () => {
  const navigation = useNavigation<any>();
  const { redesign } = useAdminColors();
  const [selectedConversation, setSelectedConversation] = useState<number | undefined>(undefined);

  // Chat options dropdown state
  const [isChatOptionsOpen, setIsChatOptionsOpen] = useState(false);
  const chatOptionsButtonRef = useRef<View | null>(null);
  const [chatOptionsAnchor, setChatOptionsAnchor] = useState<AnchorRect | null>(null);

  // Desktop side panel state for details
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);

  // Thread Owner modal
  const [showThreadOwnerModal, setShowThreadOwnerModal] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 250);

  // Conversations state & search
  const {
    conversations,
    isLoading,
    error,
    setConversationQuery,
    hasNextPage,
    loadMore,
    filterType,
    setFilterType,
    sortType,
    setSortType,
    markConversationAsReadLocally,
  } = useConversations();

  const { sortOptions } = useMessageFilters();

  const [archiveConversation, { isLoading: isArchiving }] = useArchiveConversationMutation();
  const { showSuccess } = useToast();
  const { showApiError } = useErrorToast();

  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);

  const hasData = (conversations || []).length > 0;

  const { data: conversationData } = useGetConversationQuery(
    { conversationId: selectedConversation as number },
    { skip: !selectedConversation, pollingInterval: 0, refetchOnMountOrArgChange: true }
  );
  const conversation = conversationData?.response;

  const onCreateMessage = useCallback(() => {
    navigation.navigate('CreateMessage');
  }, [navigation]);

  const onEditConversation = useCallback(() => {
    if (selectedConversation) {
      navigation.navigate('EditMessage', { conversationId: selectedConversation });
    }
  }, [navigation, selectedConversation]);

  React.useEffect(() => {
    setConversationQuery(debouncedSearch);
  }, [debouncedSearch]); // Remove setConversationQuery from dependencies

  const { truncateSubject, truncateMessage, getRecipientDisplayName, formatMessageDate } = useConversationUtils();

  const renderConversationItem: ListRenderItem<IConversation> = useCallback(({ item }) => {
    const conversationAccounts = (item.accounts || []).filter(
      (acct) => acct.ConversationAccount?.type === 'resident'
    );
    const recipientCount = conversationAccounts.length;
    const isGroup = recipientCount > 1;
    const subject = truncateSubject(item.subject || '');
    const preview = truncateMessage(item.messages?.[0]?.content || '');
    const dateRaw = item.messages?.[0]?.createdAt || item.createdAt;
    const dateLabel = dateRaw ? formatMessageDate(dateRaw) : '';
    const subtitle = getRecipientDisplayName(item);

    return (
      <ChatGlassConversationCard
        id={item.id}
        subject={subject}
        preview={preview}
        date={dateLabel}
        isGroup={isGroup}
        recipientCount={recipientCount}
        hasUnread={!!item.hasUnread}
        isSelected={selectedConversation === item.id}
        subtitle={subtitle}
        onPress={(id) => {
          markConversationAsReadLocally(Number(id));
          setSelectedConversation(Number(id));
        }}
      />
    );
  }, [
    selectedConversation,
    markConversationAsReadLocally,
    truncateSubject,
    truncateMessage,
    formatMessageDate,
    getRecipientDisplayName,
  ]);

  const openChatOptions = useCallback(() => {
    if (chatOptionsButtonRef.current) {
      chatOptionsButtonRef.current.measureInWindow((x, y, width, height) => {
        const anchor: AnchorRect = { x, y, w: width, h: height };
        setChatOptionsAnchor(anchor);
        setIsChatOptionsOpen(true);
      });
    }
  }, []);

  const closeChatOptions = useCallback(() => {
    setIsChatOptionsOpen(false);
  }, []);

  const handleArchive = useCallback(async () => {
    if (!conversation) return;
    if (Platform.OS === 'web') {
      setArchiveConfirmOpen(true);
      return;
    }
    // Mobile/native fallback - use cross-platform confirmation
    const confirmed = await showConfirm(
      'Archive Conversation',
      'Are you sure you want to archive this conversation? It will be moved to the archived section.',
      'Archive',
      'Cancel'
    );

    if (!confirmed) return;

    try {
      await archiveConversation(conversation.id).unwrap();
      showSuccess('Conversation archived successfully');
      closeChatOptions();
    } catch (error: any) {
      showApiError(error?.data?.message || 'Failed to archive conversation');
    }
  }, [archiveConversation, conversation, showApiError, showSuccess, closeChatOptions]);

  // Filter tab items
  const filterItems: TabSegmentItem<FilterType>[] = useMemo(() => {
    return (Object.keys(FILTER_VALUES) as FilterType[]).map((key) => ({
      key,
      label: FILTER_VALUES[key],
    }));
  }, []);

  // Sort options for ChatGlassListHeader
  const headerSortOptions = useMemo(() =>
    sortOptions.map((opt) => ({ key: opt.key, label: opt.value })),
  [sortOptions]);

  const renderLeftPane = () => {
    let content: React.ReactNode;

    if (isLoading && !hasData) {
      content = <ConversationListSkeleton />;
    } else if (error && !hasData) {
      content = (
        <View style={styles.errorWrap}>
          <InlineEmptyMessage
            title={'Unable to Load Conversations'}
            message={'Something went wrong while loading the conversations.'}
            icon={'X_CIRCLE' as any}
          />
        </View>
      );
    } else if (!hasData) {
      // Filter-specific empty states + search no-results
      const trimmedQuery = (searchQuery || '').trim();
      const cfg = trimmedQuery.length > 0
        ? getNoResultsState(trimmedQuery)
        : getFilterEmptyState(filterType);
      content = (
        <InlineEmptyMessage
          title={cfg.title}
          message={cfg.message}
          icon={cfg.icon}
          containerStyle={styles.emptyContainer}
          titleStyle={{ ...styles.emptyTitle, color: redesign.text.primary }}
          messageStyle={{ ...styles.emptyMessage, color: redesign.text.muted }}
        />
      );
    } else {
      content = (
        <FlatList
          data={conversations}
          keyExtractor={(item) => String(item.id)}
          renderItem={renderConversationItem}
          style={styles.list}
          contentContainerStyle={styles.listContent}
          onEndReachedThreshold={0.5}
          onEndReached={() => {
            if (hasNextPage && !isLoading) {
              loadMore();
            }
          }}
        />
      );
    }

    return (
      <View style={styles.leftContent}>
        <ChatGlassListHeader<string>
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortOptions={headerSortOptions}
          sortOption={sortType as string}
          onSortChange={(key) => setSortType(key as typeof sortType)}
          placeholder="Search conversations"
        />
        <View style={styles.filtersWrap}>
          <TabSegment<FilterType>
            items={filterItems}
            selectedKey={filterType}
            onChange={(key) => {
              if (key !== filterType) {
                setSelectedConversation(undefined);
                setFilterType(key);
              }
            }}
            variant="pill"
            size="md"
          />
        </View>
        {content}
      </View>
    );
  };

  const renderRightPane = () => {
    if (!selectedConversation) {
      const cfg = EMPTY_MESSAGES.messages.default;
      return (
        <View style={styles.placeholderWrap}>
          <InlineEmptyMessage
            title={cfg.title}
            message={cfg.message}
            icon={cfg.icon}
          />
        </View>
      );
    }

    return (
      <View style={styles.chatWrap}>
        {/* Chat Header */}
        {conversation && (
          <View
            style={[
              styles.chatHeader,
              { borderBottomColor: redesign.card.border },
            ]}
          >
            <View style={styles.chatHeaderText}>
              <Text style={[styles.chatHeaderTitle, { color: redesign.text.primary }]} numberOfLines={1}>
                {conversation.subject}
              </Text>
              <Text style={[styles.chatHeaderSubtitle, { color: redesign.text.muted }]} numberOfLines={1}>
                {getRecipientDisplayName(conversation)}
              </Text>
            </View>
            <TouchableOpacity
              ref={chatOptionsButtonRef}
              style={styles.chatMenuButton}
              onPress={openChatOptions}
              activeOpacity={0.8}
            >
              <SVG_COMPONENTS.EDIT_DOT width={24} height={24} color={redesign.text.primary} />
            </TouchableOpacity>
          </View>
        )}

        <View style={styles.chatBody}>
          <ChatWindow conversationId={selectedConversation} />
        </View>
      </View>
    );
  };

  return (
    <DesktopAdminLayout>
      <DesktopPageHeader title="Messages">
        <TouchableOpacity
          style={styles.composeButton}
          onPress={onCreateMessage}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel="New message"
          accessibilityHint="Tap to start a new message"
        >
          <SVG_COMPONENTS.PENCIL width={20} height={20} color={PRIMITIVE_COLORS.white} />
        </TouchableOpacity>
      </DesktopPageHeader>

      <View style={styles.row}>
        {/* List pane is intentionally a plain View, not GlassCard, to avoid glass-on-glass stacking inside ChatGlassListHeader/Card */}
        <View style={[styles.leftPane, { backgroundColor: redesign.bg.base, borderColor: redesign.card.border }]}>
          {renderLeftPane()}
        </View>
        <GlassCard padding={0} style={styles.rightPane}>
          {renderRightPane()}
        </GlassCard>
      </View>

      <DesktopChatOptions
        isOpen={isChatOptionsOpen}
        anchor={chatOptionsAnchor}
        onClose={closeChatOptions}
        onShowDetails={() => setShowDetailsPanel(true)}
        onEdit={onEditConversation}
        onArchive={handleArchive}
        onThreadOwner={() => setShowThreadOwnerModal(true)}
        isArchiving={isArchiving}
      />

      {/* Desktop conversation details side panel */}
      <DesktopSidePanel visible={showDetailsPanel} onClose={() => setShowDetailsPanel(false)} width={420}>
        {conversation && (
          <ConversationDetailsPanel conversation={conversation} onClose={() => setShowDetailsPanel(false)} />
        )}
      </DesktopSidePanel>

      {/* Thread Owner Modal */}
      {conversation && (
        <ThreadOwnerModal
          visible={showThreadOwnerModal}
          onClose={() => setShowThreadOwnerModal(false)}
          conversation={conversation}
        />
      )}

      {/* Web confirm modal for archiving */}
      <WebConfirmModal
        visible={archiveConfirmOpen}
        title={'Archive Conversation'}
        message={'Are you sure you want to archive this conversation? It will be moved to the archived section.'}
        confirmText={'Archive'}
        cancelText={'Cancel'}
        onConfirm={async () => {
          if (!conversation) return;
          try {
            await archiveConversation(conversation.id).unwrap();
            showSuccess('Conversation archived successfully');
            setArchiveConfirmOpen(false);
            closeChatOptions();
          } catch (error: any) {
            showApiError(error?.data?.message || 'Failed to archive conversation');
            setArchiveConfirmOpen(false);
          }
        }}
        onCancel={() => setArchiveConfirmOpen(false)}
      />
    </DesktopAdminLayout>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 12,
    flex: 1,
    minHeight: 360,
  },
  leftPane: {
    width: 360,
    maxWidth: 420,
    alignSelf: 'stretch',
    borderWidth: 1,
    borderRadius: 12,
    overflow: 'hidden',
  },
  leftContent: {
    flex: 1,
    paddingTop: 4,
  },
  rightPane: {
    flex: 1,
    alignSelf: 'stretch',
  },
  errorWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 320,
    padding: 24,
  },
  list: {
    flex: 1,
  },
  listContent: {
    paddingVertical: 8,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 24,
  },
  emptyTitle: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  emptyMessage: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    maxWidth: 360,
  },
  placeholderWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  chatWrap: {
    flex: 1,
  },
  chatHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  chatHeaderText: {
    flex: 1,
    marginRight: 12,
  },
  chatHeaderTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 2,
  },
  chatHeaderSubtitle: {
    fontSize: 14,
  },
  chatMenuButton: {
    padding: 8,
  },
  chatBody: {
    flex: 1,
  },
  filtersWrap: {
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 8,
  },
  composeButton: {
    width: 40,
    height: 40,
    borderRadius: 12,
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
